/**
 * Safely parse a date string, handling YYYY-MM-DD format as local date
 * to avoid timezone shifts
 */
export function parseDate(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;
  // If it's already in YYYY-MM-DD format, parse as local date to avoid timezone shifts
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  }
  // For ISO datetime strings (e.g., "2025-11-19T00:00:00.000Z"), extract date part and parse as local
  const isoDateMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})T/);
  if (isoDateMatch) {
    const [year, month, day] = isoDateMatch[1].split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  }
  // For other formats, parse normally
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  return date;
}

export function toYMD(isoOrNull: string | null): string | null {
  if (!isoOrNull) return null;
  // Handle date strings that are already in YYYY-MM-DD format
  if (isoOrNull.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return isoOrNull;
  }
  const d = new Date(isoOrNull);
  if (isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10); // 'YYYY-MM-DD' in UTC
}

export function addBusinessDaysYMD(startYmd: string, days: number): string {
  const d = new Date(startYmd + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function calcEndYMD(startYmd: string, estHours: number | null | undefined): string {
  const hours = Math.max(1, Math.floor(estHours ?? 4));
  const days = Math.ceil(hours / 8);
  return addBusinessDaysYMD(startYmd, days);
}

// Unified date formatting functions to replace duplicates across components
export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '';
  // If it's already in YYYY-MM-DD format, parse as local date to avoid timezone shifts
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  // For ISO datetime strings (e.g., "2025-11-19T00:00:00.000Z"), extract date part and parse as local
  const isoDateMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})T/);
  if (isoDateMatch) {
    const [year, month, day] = isoDateMatch[1].split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  // For other formats, parse normally
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatTime(timeString: string | null | undefined): string {
  if (!timeString) return '';
  try {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  } catch {
    return timeString;
  }
}

export function formatDateForInput(dateString: string | null | undefined): string {
  if (!dateString) return '';
  // If it's already in YYYY-MM-DD format, return as-is
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dateString;
  }
  // For ISO datetime strings, extract date part
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  // Use UTC methods to preserve the date value
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function formatDateWithoutYear(dateString: string | null | undefined): string {
  if (!dateString) return '';
  // If it's already in YYYY-MM-DD format, parse as local date to avoid timezone shifts
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }
  // For ISO datetime strings (e.g., "2025-11-19T00:00:00.000Z"), extract date part and parse as local
  const isoDateMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})T/);
  if (isoDateMatch) {
    const [year, month, day] = isoDateMatch[1].split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }
  // For other formats, parse normally
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}
